import { User, useUserAddress } from "@/lib/api/user.ts";
import {
  getPreferredName,
  renderRedactableField,
} from "@/lib/redacted-display";
import { cn } from "@/lib/utils";
import {
  MapIcon,
  MapPinIcon,
  PhoneIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { formatDistanceToNow } from "date-fns";
import { Loader2 } from "lucide-react";

interface EscalationTeenDetailsProps {
  mentee: User;
}

interface InformationBlockProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  subtext?: string | null;
  iconBgColor?: "lime" | "red" | "indigo" | "rose";
}

function InformationBlock({
  icon,
  label,
  value,
  subtext,
  iconBgColor,
}: InformationBlockProps) {
  return (
    <div className="flex items-center justify-start gap-4">
      <div
        className={cn(
          "flex h-10 w-10 items-center justify-center gap-1 rounded-full p-2",
          {
            "bg-lime-50": iconBgColor === "lime",
            "bg-red-50": iconBgColor === "red",
            "bg-indigo-50": iconBgColor === "indigo",
            "bg-rose-50": iconBgColor === "rose",
          },
        )}
      >
        <div className="flex h-6 w-6 items-center justify-center">{icon}</div>
      </div>
      <div className="inline-flex shrink grow basis-0 flex-col items-start justify-center gap-1">
        <div className="text-base font-normal leading-snug text-stone-500">
          {label}
        </div>
        <div className="self-stretch text-base font-bold leading-tight text-black">
          {value}
        </div>
        {!!subtext && (
          <div className="text-xs font-normal leading-none text-green-900">
            {subtext}
          </div>
        )}
      </div>
    </div>
  );
}

export function EscalationTeenDetails({ mentee }: EscalationTeenDetailsProps) {
  const { data: address, isLoading: isLoadingAddress } = useUserAddress(
    mentee?.id,
  );

  if (!address || isLoadingAddress) {
    return (
      <div className="flex min-h-12 items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-slate-900" />
      </div>
    );
  }

  if (!mentee) {
    return (
      <div className="text-lg font-bold text-slate-900">
        No teen details found
      </div>
    );
  }

  const redactedFields = mentee.redacted_fields;
  const teenName = getPreferredName(mentee) || "Teen";

  return (
    <div className="flex flex-col">
      <h2 className="mb-12 text-2xl font-bold">
        If your teen has done something to hurt themselves or someone else,
        please call 911
      </h2>

      <div className="space-y-8">
        <InformationBlock
          icon={<PhoneIcon className="h-6 w-6 text-lime-950" />}
          label={`${teenName}'s Phone Number`}
          value={renderRedactableField("phone", mentee.phone, redactedFields, "-")}
          iconBgColor="lime"
        />

        <InformationBlock
          icon={<MapIcon className="h-6 w-6 text-blue-700" />}
          label="Home Address"
          value={renderRedactableField(
            "location",
            mentee?.location,
            redactedFields,
            "Address not available",
          )}
          iconBgColor="indigo"
        />

        <InformationBlock
          icon={<MapPinIcon className="h-6 w-6 text-blue-700" />}
          label="Live Location"
          value={
            address?.isFallback ? "Location not available" : address?.address
          }
          subtext={
            !address?.isFallback && mentee?.metadata?.location?.updated_at
              ? `${formatDistanceToNow(new Date(mentee.metadata.location.updated_at))} ago`
              : null
          }
          iconBgColor="indigo"
        />

        <InformationBlock
          icon={<ShieldCheckIcon className="h-6 w-6 text-pink-800" />}
          label="Emergency Contact"
          value={renderRedactableField("guardian_name", mentee.guardian_name, redactedFields, "-")}
          iconBgColor="rose"
        />

        <InformationBlock
          icon={<PhoneIcon className="h-6 w-6 text-pink-800" />}
          label="Emergency Contact Phone"
          value={renderRedactableField(
            "emergency_phone",
            mentee.emergency_phone,
            redactedFields,
            "-",
          )}
          iconBgColor="rose"
        />
      </div>
    </div>
  );
}
